import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, MessageCircle, Loader2 } from "lucide-react";
import { SITE, whatsappLink } from "@/lib/site";

const goals = [
  "Reduce pain & improve posture",
  "Sustainable fat loss",
  "Build functional strength",
  "Improve mobility & flexibility",
  "General body transformation",
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Consult = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    goal: goals[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in name, email, and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thanks! Anoop will reach out shortly.");
      setForm({ name: "", email: "", phone: "", goal: goals[0], message: "" });
    } catch (err) {
      toast.error("Couldn't send. Please WhatsApp directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="consult"
      data-testid="consult-section"
      className="relative py-20 sm:py-28 lg:py-36 border-t border-white/5 overflow-hidden"
    >
      <div className="aurora" style={{ background: "#0055FF", width: 600, height: 600, top: -200, left: "30%", opacity: 0.25 }}></div>
      <div className="aurora" style={{ background: "#00E5FF", width: 500, height: 500, bottom: -200, right: -100, opacity: 0.2 }}></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <div className="text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-4">Book Consultation</div>
          <h2 className="font-display text-4xl lg:text-5xl xl:text-6xl font-extrabold leading-[1.05] tracking-[-0.02em] text-white">
            Start your <span className="text-gradient-blue">transformation.</span>
          </h2>
          <p className="mt-5 text-zinc-300/90 text-lg max-w-md">
            Tell us about your goals. Anoop will personally reach out to schedule your
            assessment and discuss the right plan for you.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="consult-whatsapp-card"
              className="flex items-center gap-4 p-5 rounded-2xl glass glass-hover group"
            >
              <div className="w-12 h-12 rounded-xl bg-[#25D366]/15 border border-[#25D366]/30 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-[#25D366]" />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">WhatsApp Direct</div>
                <div className="text-white font-display font-semibold text-base mt-0.5">
                  {SITE.whatsappDisplay}
                </div>
              </div>
              <ArrowUpRight className="w-5 h-5 text-zinc-500 group-hover:text-[#00E5FF] transition-colors" />
            </a>
            <a
              href={SITE.instagramUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="consult-instagram-card"
              className="flex items-center gap-4 p-5 rounded-2xl glass glass-hover group"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0055FF]/20 to-[#00E5FF]/20 border border-white/10 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-[#00E5FF]" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="2" width="20" height="20" rx="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="18" cy="6" r="1" fill="currentColor" />
                </svg>
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Instagram</div>
                <div className="text-white font-display font-semibold text-base mt-0.5">
                  @{SITE.instagram}
                </div>
              </div>
              <ArrowUpRight className="w-5 h-5 text-zinc-500 group-hover:text-[#00E5FF] transition-colors" />
            </a>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          onSubmit={submit}
          data-testid="consult-form"
          className="lg:col-span-7 rounded-3xl p-7 lg:p-10 glass relative"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Name</label>
              <input
                value={form.name}
                onChange={update("name")}
                required
                placeholder="Your full name"
                data-testid="consult-input-name"
                className="mt-2 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 outline-none focus:border-[#00E5FF]/50 transition-colors"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={update("email")}
                required
                placeholder="you@email.com"
                data-testid="consult-input-email"
                className="mt-2 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 outline-none focus:border-[#00E5FF]/50 transition-colors"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Phone / WhatsApp</label>
              <input
                value={form.phone}
                onChange={update("phone")}
                required
                placeholder="+971 ..."
                data-testid="consult-input-phone"
                className="mt-2 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 outline-none focus:border-[#00E5FF]/50 transition-colors"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Primary Goal</label>
              <select
                value={form.goal}
                onChange={update("goal")}
                data-testid="consult-input-goal"
                className="mt-2 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3.5 text-white outline-none focus:border-[#00E5FF]/50 transition-colors"
              >
                {goals.map((g) => (
                  <option key={g} value={g} className="bg-black">{g}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-400">Tell Anoop a bit more (optional)</label>
              <textarea
                value={form.message}
                onChange={update("message")}
                rows={4}
                placeholder="Past injuries, preferred schedule, etc."
                data-testid="consult-input-message"
                className="mt-2 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-zinc-600 outline-none focus:border-[#00E5FF]/50 transition-colors resize-none"
              ></textarea>
            </div>
          </div>

          <div className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4">
            <button
              type="submit"
              disabled={loading}
              data-testid="consult-submit-btn"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#0055FF] to-[#00E5FF] text-black px-7 py-4 rounded-full font-semibold tracking-wide hover:shadow-[0_0_30px_-5px_rgba(0,229,255,0.55)] transition-shadow disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? "Sending..." : "Request Consultation"}
              {!loading && <ArrowUpRight className="w-4 h-4" />}
            </button>
            <span className="text-xs text-zinc-500">
              Or message directly via WhatsApp for the fastest response.
            </span>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default Consult;
